import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { parseISO } from 'date-fns';
import { Calendar, Plus } from 'lucide-react';
import { COLORS } from '@/data';
import type { ScheduledActivity, Clip } from '@/types';
import 'react-day-picker/style.css';

interface CalendarViewProps {
  scheduledActivities: ScheduledActivity[];
  clips: Clip[]; // Used for future features like showing clip info on hover
  onDateSelect: (date: Date) => void;
  onScheduleNew: () => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  scheduledActivities,
  clips: _clips, // Reserved for future use
  onDateSelect,
  onScheduleNew,
}) => {
  const [month, setMonth] = useState(new Date());

  // Get dates with scheduled activities
  const scheduledDates = scheduledActivities
    .filter(activity => !activity.completedAt)
    .map(activity => parseISO(activity.scheduledDate));

  // Get dates with completed activities
  const completedDates = scheduledActivities
    .filter(activity => activity.completedAt)
    .map(activity => parseISO(activity.scheduledDate));

  // Custom CSS for dark theme and indicators
  const customStyles = `
    .calendar-dark {
      --rdp-accent-color: ${COLORS.lime};
      --rdp-accent-background-color: rgba(212, 255, 31, 0.15);
      --rdp-range_middle-background-color: rgba(212, 255, 31, 0.1);
    }
    .calendar-dark .rdp-root {
      --rdp-day-font: 400 14px system-ui;
      --rdp-day-height: 44px;
      --rdp-day-width: 44px;
      background: transparent;
    }
    .calendar-dark .rdp-month_caption {
      font-weight: 600;
      color: white;
      justify-content: center;
      padding: 0.5rem 0;
    }
    .calendar-dark .rdp-nav {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: space-between;
      padding: 0.5rem;
    }
    .calendar-dark .rdp-button_previous,
    .calendar-dark .rdp-button_next {
      color: #a1a1aa;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .calendar-dark .rdp-button_previous:hover,
    .calendar-dark .rdp-button_next:hover {
      background: #3f3f46;
      color: white;
    }
    .calendar-dark .rdp-weekday {
      color: #71717a;
      font-size: 0.75rem;
      font-weight: 500;
    }
    .calendar-dark .rdp-day {
      color: #e4e4e7;
      border-radius: 10px;
    }
    .calendar-dark .rdp-day:hover:not(.rdp-disabled):not(.rdp-selected) {
      background: #3f3f46;
    }
    .calendar-dark .rdp-today {
      border: 2px solid ${COLORS.lime};
      font-weight: 600;
    }
    .calendar-dark .rdp-selected {
      background: ${COLORS.lime} !important;
      color: #18181b !important;
      font-weight: 600;
    }
    .calendar-dark .rdp-outside {
      color: #52525b;
    }
    .calendar-dark .rdp-disabled {
      color: #3f3f46;
    }
    /* Custom modifier styles */
    .calendar-dark .rdp-day_scheduled {
      position: relative;
    }
    .calendar-dark .rdp-day_scheduled::after {
      content: '';
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #f97316;
    }
    .calendar-dark .rdp-day_completed {
      position: relative;
    }
    .calendar-dark .rdp-day_completed::after {
      content: '';
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #22c55e;
    }
    .calendar-dark .rdp-day_scheduled.rdp-day_completed::after {
      width: 12px;
      background: linear-gradient(90deg, #f97316 50%, #22c55e 50%);
    }
  `;

  return (
    <div className="bg-zinc-800/50 rounded-2xl border border-zinc-700/50">
      <style>{customStyles}</style>

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-700/50">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5" style={{ color: COLORS.lime }} />
          <h2 className="text-lg font-semibold text-white">Plan Your Dates</h2>
        </div>
        <button
          onClick={onScheduleNew}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-900"
          style={{ backgroundColor: COLORS.lime }}
        >
          <Plus className="w-4 h-4" /> Schedule
        </button>
      </div>

      {/* Calendar */}
      <div className="p-4 calendar-dark">
        <DayPicker
          mode="single"
          month={month}
          onMonthChange={setMonth}
          onDayClick={onDateSelect}
          modifiers={{
            scheduled: scheduledDates,
            completed: completedDates,
          }}
          modifiersClassNames={{
            scheduled: 'rdp-day_scheduled',
            completed: 'rdp-day_completed',
          }}
          showOutsideDays
          fixedWeeks
        />
      </div>

      {/* Legend */}
      <div className="px-4 pb-4">
        <div className="flex items-center justify-center gap-6 text-xs text-zinc-500">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <span>Scheduled</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded border-2" style={{ borderColor: COLORS.lime }} />
            <span>Today</span>
          </div>
        </div>
      </div>
    </div>
  );
};
